
import React, { useState, useContext, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import './Signup.css'; 
import Alert from '../Components/Alert';
import { LanguageContext } from '../context/LanguageContext';
import authTranslations from '../languages/auth.json';
import Logo from "../assets/image/logoo.png";
import signupImg from "../assets/image/65214547de2a3ff968379537926d89705ec4f0a1.png";

const ChevronLeft = ({ isRtl }) => (
    <svg style={{ transform: isRtl ? 'rotate(180deg)' : 'none' }} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
);

export const Signup = () => {
    const { lang } = useContext(LanguageContext);
    const t = authTranslations[lang].signup;
    const isRtl = ['ar', 'ur'].includes(lang);

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [application, setApplication] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [nameError, setNameError] = useState('');

    const navigate = useNavigate();

    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info',
        position: isRtl ? 'left' : 'right',
    });

    const showAlert = (type, message) => {
        setAlertState({ open: true, type, message, position: isRtl ? 'left' : 'right' });
    };

    const onFormSubmit = async (e) => {
        e.preventDefault();
        setEmailError('');
        setNameError('');

        if (!email) {
            setEmailError(t.errorEmailRequired);
            return;
        }
        if (!/^[\w\-\.]+@([\w\-]+\.)+[\w\-]{2,4}$/.test(email)) {
            setEmailError(t.errorEmailInvalid);
            return;
        }
        if (!name.trim()) {
            setNameError(t.errorNameRequired);
            return;
        }

        setSubmitting(true);
        try {
            // Signup API call is temporarily disabled. Route requests through Contact us for now.
            // const resp = await auth.signupAction(email, name, application);
            // if (resp.success) {
            //     navigate(`/verify-code?email=${encodeURIComponent(email)}`);
            // } else {
            //     showAlert('error', t.alertSignupFailed + (resp.message || t.alertSignupUnknownError));
            // }
            navigate('/contact-us', {
                state: {
                    name: name.trim(),
                    email: email.trim(),
                    message: application,
                },
            });
        } catch (err) {
            showAlert('error', t.alertSignupGenericError);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Fragment>
            <Alert
                open={alertState.open}
                close={() => setAlertState({ ...alertState, open: false })}
                message={alertState.message}
                type={alertState.type}
                position={alertState.position}
            />

            <div className="auth-page-wrapper" dir={isRtl ? "rtl" : "ltr"}>
                <div className="auth-brand-header">
                    <img src={Logo} alt="Sama-IOT Logo" className="auth-brand-logo" />
                    <span className="auth-brand-name">Sama-IOT</span>
                </div>

                <div className="signup-card-center">
                    <div className="signup-main-card">
                        <div className="signup-form-area">
                            <button className="signup-back-button" onClick={() => navigate(-1)}>
                                <ChevronLeft isRtl={isRtl} />
                                {t.back}
                            </button>

                            <div className="signup-header-text">
                                <h2>{t.pageTitle}</h2>
                                <p className="auth-subtitle">{t.formTitle}</p>
                            </div>

                            <form onSubmit={onFormSubmit}>
                                <div className="auth-form-group">
                                    <label htmlFor="user-email">{t.labelEmail}</label>
                                    <div className={`auth-input-wrapper ${emailError ? 'has-error' : ''}`}>
                                        <input
                                            id="user-email"
                                            type="email"
                                            value={email}
                                            placeholder={t.emailPlaceholder}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="auth-form-input"
                                            autoComplete="off"
                                        />
                                    </div>
                                    {emailError && <span className="auth-error-message">{emailError}</span>}
                                </div>

                                <div className="auth-form-group">
                                    <label htmlFor="first-name">{t.labelName}</label>
                                    <div className={`auth-input-wrapper ${nameError ? 'has-error' : ''}`}>
                                        <input
                                            id="first-name"
                                            type="text"
                                            value={name}
                                            placeholder={t.firstNamePlaceholder}
                                            onChange={(e) => setName(e.target.value)}
                                            className="auth-form-input"
                                            autoComplete="name"
                                        />
                                    </div>
                                    {nameError && <span className="auth-error-message">{nameError}</span>}
                                </div>

                                <div className="auth-form-group">
                                    <label htmlFor="application">{t.labelApplication || 'Application'}</label>
                                    <div className="auth-input-wrapper">
                                        <textarea
                                            id="application"
                                            value={application}
                                            placeholder={t.applicationPlaceholder || 'Application (optional)'}
                                            onChange={(e) => setApplication(e.target.value)}
                                            className="auth-form-input"
                                            rows={3}
                                            maxLength={4000}
                                            style={{ resize: 'vertical' }}
                                        />
                                    </div>
                                </div>
                                
                                <button className="auth-submit-btn" type="submit" disabled={submitting}>
                                    {submitting ? t.signingUp : t.buttonSignUp}
                                </button>

                                <div className="auth-divider">
                                    <span>{t.or}</span>
                                </div>

                                <div className="auth-login-footer">
                                    <span>{t.haveAccount} </span>
                                    <button
                                        className="auth-redirect-link"
                                        onClick={() => navigate("/login")}
                                        type="button"
                                    >
                                        {t.buttonLogin}
                                    </button>
                                </div>
                            </form>
                        </div>
                        
                        <div className="signup-image-area">
                            <img src={signupImg} alt="Signup Illustration" className="signup-illustration" />
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};
